<template>
	<div
		class="p-6 transition-all duration-500 border shadow-xl sm:p-8 lg:p-10 sm:shadow-2xl bg-white/90 backdrop-blur-md rounded-2xl sm:rounded-3xl border-white/40 hover:shadow-blue-500/10 animate-fade-in-up"
	>
		<div class="mb-6 text-center sm:mb-8">
			<h2 class="mb-3 text-2xl font-bold sm:text-3xl text-slate-900">
				Summary of Your Commercial Application
			</h2>
			<p class="max-w-2xl mx-auto text-base sm:text-lg text-slate-600">
				Please review the details below before proceeding to payment.
			</p>
		</div>

		<div class="max-w-4xl mx-auto space-y-8">
			<div class="p-6 border rounded-lg shadow-md bg-slate-50">
				<h3 class="pb-2 mb-4 text-xl font-bold border-b">
					Insurance Details
				</h3>
				<div
					class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
				>
					<div>
						<p class="text-sm font-medium text-gray-500">
							Cover Type
						</p>
						<p class="text-base font-semibold text-gray-900">
							{{
								selectedCoverType
									? selectedCoverType
											.charAt(0)
											.toUpperCase() +
										selectedCoverType.slice(1)
									: 'N/A'
							}}
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">
							Vehicle Value
						</p>
						<p class="text-base font-semibold text-gray-900">
							KES
							{{
								vehicleValue
									? vehicleValue.toLocaleString()
									: 'N/A'
							}}
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">
							Selected Product
						</p>
						<p class="text-base font-semibold text-gray-900">
							{{ selectedProduct ? selectedProduct.name : 'N/A' }}
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">Premium</p>
						<p class="text-base font-semibold text-gray-900">
							KES
							{{
								selectedProduct
									? selectedProduct.premium.toLocaleString()
									: 'N/A'
							}}
						</p>
					</div>
				</div>
			</div>

			<div class="p-6 border rounded-lg shadow-md bg-slate-50">
				<h3 class="pb-2 mb-4 text-xl font-bold border-b">
					Commercial Vehicle Details
				</h3>
				<div
					class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
				>
					<div>
						<p class="text-sm font-medium text-gray-500">Make</p>
						<p class="text-base font-semibold text-gray-900">
							{{ vehicleDetails.make || 'N/A' }}
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">Model</p>
						<p class="text-base font-semibold text-gray-900">
							{{ vehicleDetails.model || 'N/A' }}
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">
							Registration Number
						</p>
						<p class="text-base font-semibold text-gray-900">
							{{ vehicleDetails.registrationNumber || 'N/A' }}
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">
							Chassis Number
						</p>
						<p class="text-base font-semibold text-gray-900">
							{{ vehicleDetails.chassisNumber || 'N/A' }}
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">
							Engine Number
						</p>
						<p class="text-base font-semibold text-gray-900">
							{{ vehicleDetails.engineNumber || 'N/A' }}
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">
							Engine Capacity
						</p>
						<p class="text-base font-semibold text-gray-900">
							{{ vehicleDetails.engineCapacity || 'N/A' }} CC
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">
							Body Type
						</p>
						<p class="text-base font-semibold text-gray-900">
							{{ vehicleDetails.bodyType || 'N/A' }}
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">
							Fuel Type
						</p>
						<p class="text-base font-semibold text-gray-900">
							{{ vehicleDetails.fuelType || 'N/A' }}
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">
							Purpose of Use
						</p>
						<p class="text-base font-semibold text-gray-900">
							{{ vehicleDetails.purpose || 'N/A' }}
						</p>
					</div>
				</div>
			</div>

			<div class="p-6 border rounded-lg shadow-md bg-slate-50">
				<h3 class="pb-2 mb-4 text-xl font-bold border-b">
					Customer Details
				</h3>
				<div
					class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
				>
					<div>
						<p class="text-sm font-medium text-gray-500">
							Full Name
						</p>
						<p class="text-base font-semibold text-gray-900">
							{{ kycDetails.fullName || 'N/A' }}
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">
							ID Number
						</p>
						<p class="text-base font-semibold text-gray-900">
							{{ kycDetails.idNumber || 'N/A' }}
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">KRA PIN</p>
						<p class="text-base font-semibold text-gray-900">
							{{ kycDetails.kraPin || 'N/A' }}
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">
							Phone Number
						</p>
						<p class="text-base font-semibold text-gray-900">
							{{ kycDetails.phoneNumber || 'N/A' }}
						</p>
					</div>
					<div>
						<p class="text-sm font-medium text-gray-500">
							Email Address
						</p>
						<p class="text-base font-semibold text-gray-900">
							{{ kycDetails.email || 'N/A' }}
						</p>
					</div>
				</div>
			</div>
		</div>

		<div
			class="flex flex-col-reverse justify-between gap-4 mt-8 sm:flex-row sm:mt-10"
		>
			<button
				@click="previous"
				class="w-full px-6 py-3 text-base font-semibold text-blue-600 transition-colors duration-300 border-2 border-blue-600 rounded-full sm:w-auto sm:text-lg hover:bg-blue-50"
			>
				Back
			</button>
			<button
				@click="next"
				class="w-full px-6 py-3 text-base font-semibold text-white transition-colors duration-300 bg-blue-600 rounded-full shadow-lg sm:w-auto sm:text-lg hover:bg-blue-700"
			>
				Proceed to Payment
			</button>
		</div>
	</div>
</template>

<script>
export default {
	name: 'CommercialSummaryPage',
	props: [
		'selectedCoverType',
		'vehicleValue',
		'selectedProduct',
		'vehicleDetails',
		'kycDetails',
	],
	methods: {
		next() {
			this.$emit('next');
		},
		previous() {
			this.$emit('previous');
		},
	},
};
</script>

<style scoped>
/* Scoped styles for this component */
</style>
